import { gql } from "@apollo/client";
import { Icon, Link } from "@resource/atlas";
import { atlasMapPin } from "@resource/atlas/icons";
import clsx from "clsx";
import { InterviewItemCardInterviewFragment } from "generated/schemaTypes";

import { getDisplayInfo, useExternalServiceLabelAndIcon } from "../utils";

const INTERVIEW_FRAGMENT = gql`
  fragment InterviewItemCardLocationInterviewFragment on Event {
    id
    conferenceUrl
    conferencePhone
    location {
      id
      name
      address
    }
  }
`;

function ExternalServiceLink({
  url,
  isInPast,
}: {
  url?: string | null;
  isInPast?: boolean;
}) {
  const { icon, label, textColorClassname } = useExternalServiceLabelAndIcon({
    url,
    isInPast,
  });

  if (!url || !icon) {
    return null;
  }

  const { displayLabel, href } = getDisplayInfo({
    url,
    label,
  });

  return (
    <Link
      as="a"
      href={href}
      target="_blank"
      className={textColorClassname ?? ""}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex gap-2 items-center">
        <Icon
          content={icon}
          className={clsx("w-5 text-subtle", {
            "grayscale opacity-60": isInPast,
          })}
        />
        <p className="text-body-md">{displayLabel}</p>
      </div>
    </Link>
  );
}

export function LocationSection({
  interview,
  isInPast,
}: {
  interview: InterviewItemCardInterviewFragment;
  isInPast?: boolean;
}) {
  const shouldShowExternalServiceLink =
    !!interview.conferenceUrl || !!interview.conferencePhone;
  const shouldShowLocationSection =
    !!interview.location || shouldShowExternalServiceLink;
  if (!shouldShowLocationSection) {
    return null;
  }

  return (
    <section className="flex gap-3 md:gap-4 flex-wrap">
      <ExternalServiceLink url={interview.conferenceUrl} isInPast={isInPast} />
      {!interview.conferenceUrl && (
        <ExternalServiceLink
          url={interview.conferencePhone}
          isInPast={isInPast}
        />
      )}
      {interview.location && (
        <div className="flex gap-[0.672rem] text-subtle items-center">
          <Icon content={atlasMapPin} className="w-5" />
          <p className="text-start text-body-md">
            {interview.location.address}
          </p>
        </div>
      )}
    </section>
  );
}

LocationSection.fragments = {
  interview: INTERVIEW_FRAGMENT,
};
